import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { FiX } from 'react-icons/fi';

const SESSION_KEY = 'youdle-community-flyer-nudge-shown';
const DISMISS_KEY = 'youdle-community-flyer-nudge-dismissed-at';
const WAIT_MS = 60_000;
const DISMISS_MS = 24 * 60 * 60 * 1000;

type FeaturedFlyer = {
  merchantSlug: string;
  title: string;
  url: string;
  startsOn: string;
  endsOn: string;
  zipcode?: string;
  storeName?: string;
  imageUrl?: string;
};

const WHITEHAVEN_FLYER: FeaturedFlyer = {
  merchantSlug: 'whitehaven-cash-saver',
  title: 'Cash Saver Whitehaven: featured grocery finds',
  url: '/flyers/cash-saver-whitehaven-shelfie',
  startsOn: '2026-10-22',
  endsOn: '2026-11-04',
  zipcode: '38116',
  storeName: 'Cash Saver Whitehaven',
  imageUrl: '/partner-flyers/cash-saver-whitehaven-shelfie-2026-10-22.webp',
};

const INTERNAL_VIEWER = /^\/flyers\/[a-z0-9-]+$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const ZIP = /^\d{5}$/;

function todayInChicago() {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
function isNearby(flyer: FeaturedFlyer, zipcode?: string | null) {
  if (!ZIP.test(zipcode || '')) return false; // Never promote Memphis-only flyers to shoppers without a verified local ZIP.
  if (!ZIP.test(flyer.zipcode || '')) return false;
  // A Whitehaven offer should not appear across all Memphis ZIP codes.
  return flyer.zipcode === zipcode;
}
function isSafeFlyer(flyer: FeaturedFlyer) {
  return INTERNAL_VIEWER.test(flyer.url) &&
    DATE.test(flyer.startsOn) && DATE.test(flyer.endsOn);
}

/**
 * Non-modal flyer discovery, not a repeating interruption.
 * Show only an active, locally relevant flyer with an in-platform viewer.
 * Keep the GOAT help launcher accessible beneath this card.
 */
export default function CommunityFlyerNudge({
  zipcode, suppressed,
}: { zipcode?: string | null; suppressed?: boolean }) {
  const [today, setToday] = useState('');
  const [flyers, setFlyers] = useState<FeaturedFlyer[]>([WHITEHAVEN_FLYER]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setToday(todayInChicago());
    const ticker = window.setInterval(() => setToday(todayInChicago()), 60 * 60 * 1000);
    return () => window.clearInterval(ticker);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/partners/flyers', { signal: controller.signal })
      .then(async response => response.ok ? response.json() : null)
      .then(data => {
        if (controller.signal.aborted || !Array.isArray(data?.flyers)) return;
        const upcoming = (data.flyers as Partial<FeaturedFlyer>[]).filter(
          (item): item is FeaturedFlyer =>
            typeof item?.merchantSlug === 'string' &&
            typeof item?.title === 'string' && item.title.length > 0 && item.title.length < 240 &&
            typeof item?.url === 'string' &&
            typeof item?.startsOn === 'string' && typeof item?.endsOn === 'string' &&
            isSafeFlyer(item as FeaturedFlyer) &&
            item.merchantSlug !== WHITEHAVEN_FLYER.merchantSlug,
        );
        setFlyers([WHITEHAVEN_FLYER, ...upcoming]);
      })
      .catch(() => { /* Keep the verified local flyer if the merchant service is unavailable. */ });
    return () => controller.abort();
  }, []);

  const flyer = flyers.find(item =>
    isSafeFlyer(item) && Boolean(today) &&
    item.startsOn <= today && item.endsOn >= today && isNearby(item, zipcode));
  const hasNearbyZip = ZIP.test(zipcode || '');
  const localLabel = hasNearbyZip ? 'Specials near you' : 'Featured Memphis specials';

  useEffect(() => {
    if (suppressed || !flyer) return;
    try {
      if (window.sessionStorage.getItem(SESSION_KEY)) return;
      const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;
    } catch { return; } // Private browsing should never lead to repeated interruptions.

    const timer = window.setTimeout(() => {
      if (document.visibilityState !== 'visible') return;
      const focused = document.activeElement;
      if (focused && (
        focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' ||
        focused.tagName === 'SELECT' || (focused as HTMLElement).isContentEditable
      )) return; // Never interrupt someone typing.
      try { window.sessionStorage.setItem(SESSION_KEY, '1'); }
      catch { return; }
      setVisible(true);
    }, WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [suppressed, flyer?.url]);

  useEffect(() => { if (suppressed) setVisible(false); }, [suppressed]);

  const dismiss = () => {
    setVisible(false);
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); }
    catch { /* Even with storage blocked, the card closes. */ }
  };

  if (!visible || suppressed || !flyer) return null;

  return <aside className="community-flyer-nudge" aria-label="Local grocery flyer specials">
    <div className="community-flyer-nudge-head">
      <span className="community-flyer-nudge-tag">GROCERY SPECIALS</span>
      <button type="button" onClick={dismiss} aria-label="Close flyer for today" title="Close flyer">
        <FiX aria-hidden="true" size={18} /> Close
      </button>
    </div>
    <Link href={flyer.url} onClick={dismiss} className="community-flyer-nudge-preview" aria-label={localLabel + ': ' + flyer.storeName}>
      {flyer.imageUrl ? <Image src={flyer.imageUrl} alt={flyer.title + ' flyer preview'} width={76} height={94}
        className="community-flyer-nudge-thumb" /> : <span className="community-flyer-nudge-placeholder">Store flyer</span>}
      <span className="community-flyer-nudge-description">
        <strong>{localLabel}</strong>
        <span>{flyer.storeName || 'Featured grocery store'} · {flyer.zipcode || 'Store location'}</span>
        <small>Valid through {flyer.endsOn} · Confirm prices with the store</small>
      </span>
    </Link>
    <div className="community-flyer-nudge-footer">
      <Link href={flyer.url} onClick={dismiss} className="community-flyer-nudge-cta">
        {hasNearbyZip ? 'See specials near you' : 'See featured specials'} ↗
      </Link>
      <button type="button" onClick={dismiss}>Not now</button>
    </div>
    <style jsx global>{`
      .community-flyer-nudge{position:fixed;right:21px;bottom:100px;z-index:60;width:min(342px,calc(100vw - 24px));padding:12px;border:1px solid #cfdfcc;border-radius:17px;background:#fff;color:#173e2f;box-shadow:0 12px 32px #173e2f2c}
      .community-flyer-nudge-head,.community-flyer-nudge-footer{display:flex;align-items:center;justify-content:space-between;gap:8px}
      .community-flyer-nudge-tag{font-size:10px;letter-spacing:.1em;font-weight:850;color:#3b7350}
      .community-flyer-nudge-head button{display:inline-flex;align-items:center;gap:4px;min-height:32px;padding:4px 8px;border:1px solid #cbdac9;border-radius:10px;background:#f5f8f2;color:#345844;font-size:11px;font-weight:800;cursor:pointer}
      .community-flyer-nudge-preview{display:flex;align-items:center;gap:11px;margin:11px 0;padding:7px;border-radius:12px;background:#f7faf5;text-decoration:none!important;color:#173e2f}
      .community-flyer-nudge-preview:hover{background:#eaf4e7}
      .community-flyer-nudge-thumb,.community-flyer-nudge-placeholder{width:76px;height:94px;flex:0 0 76px;object-fit:contain;border-radius:8px;border:1px solid #e1e9dd;background:#fff}
      .community-flyer-nudge-placeholder{display:grid;place-items:center;padding:8px;text-align:center;font-weight:800;font-size:12px}
      .community-flyer-nudge-description{display:flex;flex-direction:column;gap:6px;min-width:0}
      .community-flyer-nudge-description strong{font-size:15px;line-height:1.2}
      .community-flyer-nudge-description span{font-size:12px;color:#456b51}
      .community-flyer-nudge-description small{font-size:10px;line-height:1.3;color:#7b887b}
      .community-flyer-nudge-footer{margin-top:9px}
      .community-flyer-nudge-cta{display:inline-flex;justify-content:center;align-items:center;min-height:37px;padding:8px 12px;border-radius:10px;background:#f2c358;font-size:12px;font-weight:850;color:#183b2b;text-decoration:none!important}
      .community-flyer-nudge-cta:hover{background:#f6d580}
      .community-flyer-nudge-footer button{padding:8px 5px;border:0;background:transparent;color:#687b6c;font-size:11px;cursor:pointer}
      .community-flyer-nudge :focus-visible{outline:3px solid #e8b649;outline-offset:2px}
      @media(max-width:600px){.community-flyer-nudge{right:12px;bottom:94px;width:min(320px,calc(100vw - 24px));padding:10px}.community-flyer-nudge-description strong{font-size:13px}}
    `}</style>
  </aside>;
}
